import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Card from '../components/Card'
import CSCard from '../components/CsCard'
import SiteBody from '../components/SiteBody'
import Footer from '../components/Footer'
import { getServiceNames } from '../ServiceAPI/services/api'
import carImage from '../assets/car2.jpg'

// Import Lucide icons
import { Car, CreditCard, IdCard } from 'lucide-react'

const Dashboard = ({ openLoginModal }) => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Payment check is now handled globally in App.js

  // Fetch services when component mounts  
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await getServiceNames();
        console.log('API Response:', data); // Debug log

        // Handle different response formats
        let serviceArray = [];
        if (Array.isArray(data)) {
          serviceArray = data;
        } else if (data.services && Array.isArray(data.services)) {
          serviceArray = data.services;
        } else if (data.data && Array.isArray(data.data)) {
          serviceArray = data.data;
        }

        setServices(serviceArray);
        setLoading(false);
      } catch (err) {
        console.error('Error:', err); // Debug log
        setError('Failed to load services');
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  // handlePaymentSuccess is now handled globally in App.js

  // Map service names to their respective icons
  const serviceIcons = {
    'Vehicle Registration': Car,
    'Vehicle Insurance': CreditCard,
    'Driving License': IdCard,
  };

  // Guests can browse and apply without logging in
  const handleCardClick = (service) => {
    navigate(`/service-type/${encodeURIComponent(service.Name)}`);
  };
  return (
    <div className='bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white min-h-screen'>
      {/* Hero Section */}
      <div className='bg-gradient-to-r from-[#0b2a78] via-[#0a3ca3] to-[#0e51d8] text-white pt-12 pb-24 px-6 lg:px-8'>
        <div className='max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center'>
          {/* Left Side */}
          <div className='lg:pr-4'>
            <h1 className='text-4xl lg:text-5xl font-bold mb-4 leading-tight'>
              Your One-Stop Solution <br />
              for All <span className='text-green-400'>RTO Services</span>
            </h1>
            <p className='text-blue-100 text-lg mb-8 max-w-xl'>
              Fast, Secure & Hassle-free RTO Services Online. <br />
              We simplify vehicle & driving documentation for you.
            </p>

            {/* Feature Badges */}
            <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 max-w-2xl'>
              <div className='flex items-center gap-3'>
                <div className='bg-green-500/90 rounded-full p-2.5 flex-shrink-0'>
                  <svg className='w-4 h-4 text-white' fill='currentColor' viewBox='0 0 20 20'>
                    <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' clipRule='evenodd' />
                  </svg>
                </div>
                <div>
                  <h4 className='font-semibold text-sm text-white'>100% Digital</h4>
                  <p className='text-blue-100 text-sm'>Paperless Process</p>
                </div>
              </div>

              <div className='flex items-center gap-3'>
                <div className='bg-blue-500/90 rounded-full p-2.5 flex-shrink-0'>
                  <svg className='w-4 h-4 text-white' fill='currentColor' viewBox='0 0 20 20'>
                    <path d='M13 7H7v6h6V7z' />
                  </svg>
                </div>
                <div>
                  <h4 className='font-semibold text-sm text-white'>Fast & Reliable</h4>
                  <p className='text-blue-100 text-sm'>Quick Turnaround</p>
                </div>
              </div>

              <div className='flex items-center gap-3'>
                <div className='bg-purple-500/90 rounded-full p-2.5 flex-shrink-0'>
                  <svg className='w-4 h-4 text-white' fill='currentColor' viewBox='0 0 20 20'>
                    <path fillRule='evenodd' d='M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z' clipRule='evenodd' />
                  </svg>
                </div>
                <div>
                  <h4 className='font-semibold text-sm text-white'>Secure & Safe</h4>
                  <p className='text-blue-100 text-sm'>Your Data is Protected</p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className='flex gap-4 flex-wrap'>
              <button 
                onClick={() => handleCardClick({ Name: services[0]?.Name || 'Vehicle Registration' })}
                className='bg-gradient-to-r from-green-500 to-emerald-400 hover:from-green-600 hover:to-emerald-500 text-white font-semibold py-3 px-10 rounded-lg flex items-center gap-2 transition duration-300 shadow-lg shadow-green-700/30'
              >
                Apply for a Service
                <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                </svg>
              </button>
              <button 
              onClick={() => navigate("/my-applications")}
              className='border border-blue-300 text-white hover:bg-white hover:text-blue-900 font-semibold py-3 px-10 rounded-lg flex items-center gap-2 transition duration-300 bg-white/5'>
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <circle cx='12' cy='12' r='9' strokeWidth={2} />
                </svg>
                Track Application
              </button>
            </div>
          </div>

          {/* Right Side */}
          <div className='relative pt-2 lg:pt-0'>
            <div className='bg-gradient-to-b from-[#edf4ff] to-[#dbe9ff] rounded-2xl shadow-2xl overflow-hidden border border-blue-200/40'>
              <div className='absolute top-6 left-6 z-10 text-white'>
                <h3 className='font-bold text-2xl'>Driving a Better Tomorrow</h3>
                <p className='text-sm mt-2 max-w-sm text-white/90'>
                  RTO Bharat is your trusted partner
                  <br />
                  for digital transport services in India.
                </p>
              </div>
              <img src={carImage} alt='car' className='w-full h-[320px] object-cover object-center' />
            </div>
            
            <div className='absolute -bottom-5 left-4 right-4 bg-white rounded-xl p-4 lg:p-5 shadow-xl border border-gray-200'>
              <div className='grid grid-cols-3 gap-4'>
                <div className='text-center'>
                  <p className='text-blue-600 font-bold text-3xl leading-none'>10K+</p>
                  <p className='text-gray-500 text-xs mt-2'>Happy Customers</p>
                </div>
                <div className='text-center border-l border-r border-gray-200 px-2'>
                  <p className='text-blue-600 font-bold text-3xl leading-none'>50K+</p>
                  <p className='text-gray-500 text-xs mt-2'>Applications Processed</p>
                </div>
                <div className='text-center'>
                  <p className='text-green-500 font-bold text-3xl leading-none'>98%</p>
                  <p className='text-gray-500 text-xs mt-2'>Customer Satisfaction</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Heading */}
      <div className='mt-8 text-center'>
        <h3 className='text-2xl font-semibold text-gray-800 dark:text-white tracking-wide'>
          Our Digital RTO Services
        </h3>
        <div className='mt-3 w-[200px] h-1 bg-blue-500 mx-auto rounded'></div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center p-8 max-w-7xl mx-auto'>
        {loading ? (
          <p className='col-span-full'>Loading services...</p>
        ) : error ? (
          <p className='col-span-full'>{error}</p>
        ) : (
          services.map((service) => (
            <Card
              key={service.id}
              title={service.Name}
              content={service.description || `Apply for ${service.Name} services easily.`}
              lcolor='bg-blue-500 hover:bg-cyan-600'
              icon={serviceIcons[service.Name] || IdCard}
              onClick={() => handleCardClick(service)}
              buttonText="Get Started"
            />
          ))
        )}

        {/* Customer Support Card */}
        <CSCard
          title='Vehicle Insurance'
          content='Compare insurance plans and secure your vehicle instantly.'
          lcolor='bg-green-600 hover:bg-green-700'
          icon={CreditCard}
        />

        {/* <CSCard
          title='Vehicle Registration'
          content='Register your vehicle quickly with our hassle-free process.'
          lcolor='bg-green-600 hover:bg-green-700'
          icon={Car}
        /> */}

        {/* Global Payment Modal is now in App.js */}
      </div>

      {/* Site Body */}
      <SiteBody />

      {/* Footer */}
      <Footer />
    </div>
  )
}

export default Dashboard
